import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn, emptyToUndefined } from '@/lib/utils';
import { listPromptSets } from '@/services/promptSetService';

import type {
  CreateJobPostingPayload,
  JobPosting,
  JobPostingRole,
  UpdateJobPostingPayload,
} from '@/types/jobPosting.types';
import type { PromptSet } from '@/types/promptSet.types';

const ROLES: JobPostingRole[] = ['armed', 'unarmed', 'supervisor', 'patrol', 'event'];
const SITE_STATES = ['FL', 'NY'] as const;

const jobPostingSchema = z.object({
  title: z.string().min(1, 'titleRequired'),
  role: z.enum(['armed', 'unarmed', 'supervisor', 'patrol', 'event'], { error: 'roleRequired' }),
  siteAddress: z.string().min(1, 'siteAddressRequired'),
  siteCity: z.string().min(1, 'siteCityRequired'),
  siteState: z.enum(['FL', 'NY'], { error: 'siteStateRequired' }),
  siteZip: z.string().min(1, 'siteZipRequired'),
  description: z.string().optional(),
  requirements: z.array(z.string()).optional(),
  capacity: z.number().min(1, 'capacityMin').optional(),
  closingDate: z.string().optional(),
  promptSetId: z.string().uuid().optional(),
});

interface JobPostingFormProps {
  initialValues?: JobPosting;
  onSubmit: (data: CreateJobPostingPayload | UpdateJobPostingPayload, status?: 'draft' | 'active') => void;
  isSubmitting: boolean;
  isReadOnly?: boolean;
}

function normalizeRequirements(requirements: JobPosting['requirements']): string[] {
  const values = Array.isArray(requirements) ? requirements : requirements?.items;
  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter((item): item is string => typeof item === 'string');
}

export function JobPostingForm({ initialValues, onSubmit, isSubmitting, isReadOnly = false }: JobPostingFormProps) {
  const { t } = useTranslation();
  const isEditMode = !!initialValues;

  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [role, setRole] = useState<string>(initialValues?.role ?? '');
  const [siteAddress, setSiteAddress] = useState(initialValues?.siteAddress ?? '');
  const [siteCity, setSiteCity] = useState(initialValues?.siteCity ?? '');
  const [siteState, setSiteState] = useState<string>(initialValues?.siteState ?? '');
  const [siteZip, setSiteZip] = useState(initialValues?.siteZip ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [requirements, setRequirements] = useState<string[]>(normalizeRequirements(initialValues?.requirements));
  const [capacity, setCapacity] = useState<string>(initialValues?.capacity?.toString() ?? '');
  const [closingDate, setClosingDate] = useState<string | undefined>(initialValues?.closingDate);
  const [promptSetId, setPromptSetId] = useState<string>(initialValues?.promptSetId ?? '');
  const [promptSets, setPromptSets] = useState<PromptSet[]>([]);
  const [isLoadingPromptSets, setIsLoadingPromptSets] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let isCancelled = false;
    setIsLoadingPromptSets(true);

    listPromptSets()
      .then((items) => {
        if (!isCancelled) {
          setPromptSets(items);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPromptSets([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingPromptSets(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleAddRequirement = () => {
    setRequirements((prev) => [...prev, '']);
  };

  const handleRemoveRequirement = (index: number) => {
    setRequirements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRequirementChange = (index: number, value: string) => {
    setRequirements((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const buildPayload = (): CreateJobPostingPayload | UpdateJobPostingPayload | null => {
    const filteredRequirements = requirements.filter((r) => r.trim().length > 0);
    const raw = {
      title,
      role: emptyToUndefined(role),
      siteAddress,
      siteCity,
      siteState: emptyToUndefined(siteState),
      siteZip,
      description: emptyToUndefined(description),
      requirements: filteredRequirements.length > 0 ? filteredRequirements : undefined,
      capacity: capacity ? Number(capacity) : undefined,
      closingDate: closingDate ?? undefined,
      promptSetId: emptyToUndefined(promptSetId),
    };

    const result = jobPostingSchema.safeParse(raw);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = String(issue.path[0] ?? '');
        if (field && !fieldErrors[field]) {
          fieldErrors[field] = t(`jobPostings.validation.${issue.message}`);
        }
      }
      setErrors(fieldErrors);
      return null;
    }

    setErrors({});
    if (isEditMode) {
      if (promptSetId) {
        return {
          ...result.data,
          promptSetId,
        };
      }

      if (initialValues?.promptSetId) {
        return {
          ...result.data,
          promptSetId: null,
        };
      }
    }

    return result.data;
  };

  const handleSubmit = (status?: 'draft' | 'active') => {
    const payload = buildPayload();
    if (payload) {
      onSubmit(payload, status);
    }
  };

  const inputClassName = cn(
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm',
    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
    'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
  );

  const labelClassName = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <form
      className='space-y-6'
      onSubmit={(e) => {
        e.preventDefault();
      }}>
      {isReadOnly && initialValues && (
        <div className='rounded-md bg-yellow-50 border border-yellow-200 p-4'>
          <p className='text-sm text-yellow-800'>
            {t('jobPostings.form.readOnlyNotice', {
              status: t(`jobPostings.statuses.${initialValues.status}`),
            })}
          </p>
        </div>
      )}

      <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
        <div className='sm:col-span-2'>
          <label className={labelClassName}>{t('jobPostings.form.title')}</label>
          <input
            type='text'
            className={cn(inputClassName, errors['title'] && 'border-red-500')}
            placeholder={t('jobPostings.form.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isReadOnly}
          />
          {errors['title'] && <p className='mt-1 text-xs text-red-600'>{errors['title']}</p>}
        </div>

        <div>
          <label className={labelClassName}>{t('jobPostings.form.role')}</label>
          <select
            className={cn(inputClassName, errors['role'] && 'border-red-500')}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={isReadOnly}>
            <option value=''>{t('jobPostings.form.rolePlaceholder')}</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`jobPostings.roles.${r}`)}
              </option>
            ))}
          </select>
          {errors['role'] && <p className='mt-1 text-xs text-red-600'>{errors['role']}</p>}
        </div>

        <div>
          <label className={labelClassName}>{t('jobPostings.form.capacity')}</label>
          <input
            type='number'
            className={cn(inputClassName, errors['capacity'] && 'border-red-500')}
            placeholder={t('jobPostings.form.capacityPlaceholder')}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            disabled={isReadOnly}
            min={1}
          />
          {errors['capacity'] && <p className='mt-1 text-xs text-red-600'>{errors['capacity']}</p>}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            {t('jobPostings.form.closingDate')}{' '}
            <span className='text-gray-400 font-normal'>({t('common.optional')})</span>
          </label>
          <input
            type='date'
            value={closingDate ?? ''}
            onChange={(e) => setClosingDate(e.target.value || undefined)}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary'
            disabled={isReadOnly}
          />
          <p className='mt-1 text-xs text-gray-500'>{t('jobPostings.form.closingDateHint')}</p>
        </div>

        <div className='sm:col-span-2'>
          <label className={labelClassName}>
            {t('jobPostings.form.promptSet')}{' '}
            <span className='text-gray-400 font-normal'>({t('common.optional')})</span>
          </label>
          <select
            className={cn(inputClassName, errors['promptSetId'] && 'border-red-500')}
            value={promptSetId}
            onChange={(e) => setPromptSetId(e.target.value)}
            disabled={isReadOnly || isLoadingPromptSets}>
            <option value=''>
              {isLoadingPromptSets
                ? t('jobPostings.form.promptSetLoading')
                : t('jobPostings.form.promptSetPlaceholder')}
            </option>
            {promptSets.map((set) => (
              <option key={set.promptSetId} value={set.promptSetId}>
                {set.name}
              </option>
            ))}
          </select>
          {errors['promptSetId'] && <p className='mt-1 text-xs text-red-600'>{errors['promptSetId']}</p>}
          <p className='mt-1 text-xs text-gray-500'>{t('jobPostings.form.promptSetHint')}</p>
        </div>

        <div className='sm:col-span-2'>
          <label className={labelClassName}>{t('jobPostings.form.siteAddress')}</label>
          <input
            type='text'
            className={cn(inputClassName, errors['siteAddress'] && 'border-red-500')}
            placeholder={t('jobPostings.form.siteAddressPlaceholder')}
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
            disabled={isReadOnly}
          />
          {errors['siteAddress'] && <p className='mt-1 text-xs text-red-600'>{errors['siteAddress']}</p>}
        </div>

        <div>
          <label className={labelClassName}>{t('jobPostings.form.siteCity')}</label>
          <input
            type='text'
            className={cn(inputClassName, errors['siteCity'] && 'border-red-500')}
            placeholder={t('jobPostings.form.siteCityPlaceholder')}
            value={siteCity}
            onChange={(e) => setSiteCity(e.target.value)}
            disabled={isReadOnly}
          />
          {errors['siteCity'] && <p className='mt-1 text-xs text-red-600'>{errors['siteCity']}</p>}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className={labelClassName}>{t('jobPostings.form.siteState')}</label>
            <select
              className={cn(inputClassName, errors['siteState'] && 'border-red-500')}
              value={siteState}
              onChange={(e) => setSiteState(e.target.value)}
              disabled={isReadOnly}>
              <option value=''>{t('jobPostings.form.siteStatePlaceholder')}</option>
              {SITE_STATES.map((s) => (
                <option key={s} value={s}>
                  {t(`jobPostings.siteStates.${s}`)}
                </option>
              ))}
            </select>
            {errors['siteState'] && <p className='mt-1 text-xs text-red-600'>{errors['siteState']}</p>}
          </div>

          <div>
            <label className={labelClassName}>{t('jobPostings.form.siteZip')}</label>
            <input
              type='text'
              className={cn(inputClassName, errors['siteZip'] && 'border-red-500')}
              placeholder={t('jobPostings.form.siteZipPlaceholder')}
              value={siteZip}
              onChange={(e) => setSiteZip(e.target.value)}
              disabled={isReadOnly}
            />
            {errors['siteZip'] && <p className='mt-1 text-xs text-red-600'>{errors['siteZip']}</p>}
          </div>
        </div>

        <div className='sm:col-span-2'>
          <label className={labelClassName}>{t('jobPostings.form.description')}</label>
          <textarea
            className={cn(inputClassName, 'min-h-[100px]')}
            placeholder={t('jobPostings.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isReadOnly}
            rows={4}
          />
        </div>

        <div className='sm:col-span-2'>
          <label className={labelClassName}>{t('jobPostings.form.requirements')}</label>
          <div className='space-y-2'>
            {requirements.map((req, index) => (
              <div key={index} className='flex items-center gap-2'>
                <input
                  type='text'
                  className={inputClassName}
                  placeholder={t('jobPostings.form.requirementPlaceholder')}
                  value={req}
                  onChange={(e) => handleRequirementChange(index, e.target.value)}
                  disabled={isReadOnly}
                />
                {!isReadOnly && (
                  <button
                    type='button'
                    className='rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                    onClick={() => handleRemoveRequirement(index)}>
                    <X className='h-4 w-4' />
                  </button>
                )}
              </div>
            ))}
            {!isReadOnly && (
              <button
                type='button'
                className='flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700'
                onClick={handleAddRequirement}>
                <Plus className='h-4 w-4' />
                {t('jobPostings.form.addRequirement')}
              </button>
            )}
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <div className='flex justify-end gap-3 border-t border-gray-200 pt-6'>
          {!isEditMode ? (
            <>
              <button
                type='button'
                className={cn(
                  'rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm',
                  'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                disabled={isSubmitting}
                onClick={() => handleSubmit('draft')}>
                {t('jobPostings.form.saveAsDraft')}
              </button>
              <button
                type='button'
                className={cn(
                  'rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm',
                  'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                disabled={isSubmitting}
                onClick={() => handleSubmit('active')}>
                {t('jobPostings.form.saveAndPublish')}
              </button>
            </>
          ) : (
            <>
              {initialValues?.status === 'draft' && (
                <button
                  type='button'
                  className={cn(
                    'rounded-md border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm',
                    'hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                  disabled={isSubmitting}
                  onClick={() => handleSubmit('active')}>
                  {t('jobPostings.form.publish')}
                </button>
              )}
              <button
                type='button'
                className={cn(
                  'rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm',
                  'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                disabled={isSubmitting}
                onClick={() => handleSubmit()}>
                {t('jobPostings.form.updatePosting')}
              </button>
            </>
          )}
        </div>
      )}
    </form>
  );
}
